import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

// How far a touch has to travel past the end of an infinite slideshow before it counts as
// asking to loop around rather than as a nudge against the edge.
const OVER_SWIPE_THRESHOLD = 40;

// Slide widths land on fractional pixels often enough that a scroller resting against its
// end can report a position a pixel or so short of the maximum.
const SCROLL_BOUNDARY_TOLERANCE = 2;

// Marks the two boundary slides a seamless slideshow keeps as scenery either side of the
// real ones. Anything that counts, measures or navigates slides has to skip them.
const CLONE_ATTRIBUTE = 'data-slideshow-clone';

// View timeline names for those two, which have to be distinct from every real slide's or
// the crossfade has no timeline of its own to run on.
const CLONE_TIMELINES = { lead: '--slide-clone-lead', tail: '--slide-clone-tail' };

// How soon to look again when autoplay finds the slide it is about to move to still
// waiting on its media. Short enough that the slideshow picks up as soon as the image
// lands rather than sitting out the rest of the interval.
const AUTOPLAY_MEDIA_RETRY = 250;

// How far a pointer has to travel sideways across a crossfading slideshow to count as a
// swipe to the next or previous slide rather than a tap.
const CROSSFADE_SWIPE_THRESHOLD = 40;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('pointerdown', this.#handleScrollerPointerDown);
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      scroller.removeEventListener('touchstart', this.#handleTouchStart);
      scroller.removeEventListener('touchmove', this.#handleTouchMove);
      scroller.removeEventListener('touchend', this.#handleTouchEnd);
      scroller.removeEventListener('touchcancel', this.#handleTouchEnd);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.#handlePointerRest);
      this.removeEventListener('mouseleave', this.#handlePointerLeave);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    for (const clone of Object.values(this.#clones ?? {})) clone.remove();
    this.#clones = null;

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // A step past either end of a seamless slideshow is not a jump at all: the boundary
    // clone parked there is a copy of the slide at the other end, so it is the ordinary
    // one-frame scroll every other transition is, and it looks like one. #settleOnClone
    // moves the scroller onto the real slide once it arrives, which costs nothing on
    // screen because clone and slide are at the same point in their own timelines.
    if (
      !this.crossfade &&
      this.seamless &&
      this.#clones &&
      !instant &&
      visibleSlides.length <= 1 &&
      (requestedIndex < 0 || requestedIndex > lastIndex)
    ) {
      const clone = requestedIndex < 0 ? this.#clones.lead : this.#clones.tail;
      const target = slides[index];

      if (!target) return;

      this.#scroll.to(clone);
      this.current = index;
      this.#centerSelectedThumbnail(index);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex: current,
          userInitiated: event != null,
          trigger: 'select',
          slide: target,
          id: target.getAttribute('slide-id'),
        })
      );

      if (event) this.resume();

      return;
    }

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!this.crossfade && !instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Force Safari to recalculate the timeline state on timeline refresh (after loop).
        // Set before the slide moves back rather than a frame later: moving it re-resolves its
        // timeline, and the frame that lands in between would otherwise paint with the
        // animations half-applied.
        this.setAttribute('refreshing-timeline', '');

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    if (this.crossfade) {
      // Nothing scrolls: the slides are stacked, and marking the one that is showing is
      // what fades it in over the rest.
      this.#visibleSlides = [slide];
      for (const other of this.refs.slides) other.setAttribute('aria-hidden', `${other !== slide}`);
    } else {
      slide.setAttribute('aria-hidden', 'false');
      this.#scroll?.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );

    // A slide the visitor asked for gets a full interval of its own, rather than inheriting
    // whatever was left of the one already running — which is what made a dot press
    // sometimes move on again almost immediately.
    if (event) this.resume();
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval != null || !interval) return;

    this.paused = false;

    this.#interval = setTimeout(this.#advance, interval);
  }

  /**
   * One tick of autoplay, which schedules the next itself rather than running off a fixed
   * interval. A tick that has to hold off — the pointer is resting on the slideshow, the
   * slide coming up still has media on its way — then costs only as long as the wait,
   * instead of throwing away a whole interval and leaving the slideshow sitting still.
   */
  #advance = () => {
    this.#interval = undefined;

    const interval = this.autoplayInterval;

    if (!interval || this.paused) return;

    if (document.hidden || this.#hovered) return this.play(interval);

    // Hold rather than cross-fade to a slide whose media is still on its way: the fade would
    // land on an empty frame and read as a glitch. Warming it here as well as when it became
    // next covers a slide the slideshow has never reached.
    const upcoming = this.nextIndex;

    this.#warmSlide(upcoming);
    if (!this.#slideMediaReady(upcoming)) return this.play(AUTOPLAY_MEDIA_RETRY);

    this.next();
    this.play(interval);
  };

  /**
   * Whether a pointer that can actually hover is resting on the slideshow. Touch devices
   * report a `:hover` of their own that can stay behind after a tap, and taking that at
   * face value would stop autoplay for good.
   */
  get #hovered() {
    return this.#hoverCapable && this.matches(':hover');
  }

  get #hoverCapable() {
    return matchMedia('(hover: hover)').matches;
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearTimeout(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    // As soon as a slide becomes the next one, start fetching its media, so it has a full
    // interval to arrive rather than being asked for at the moment it is needed. Going back
    // is a step away too, and on the first slide that step lands on a boundary clone whose
    // images nothing else would have asked for.
    if (value !== this.#current) {
      this.#warmSlide(value + 1);
      if (this.#clones) this.#warmSlide(value - 1);
    }

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  /**
   * The slide at an index, wrapping in both directions so the slide after the last one is the
   * first.
   * @param {number} index
   * @returns {HTMLElement | undefined}
   */
  #slideAt(index) {
    const { slides } = this;

    if (!slides?.length) return undefined;

    return slides[((index % slides.length) + slides.length) % slides.length];
  }

  /**
   * The slide and, if it is one of the two the boundary clones copy, that clone as well —
   * because on a wrap it is the clone rather than the slide that the frame actually shows.
   * @param {number} index - Slide index; wraps.
   * @returns {HTMLElement[]}
   */
  #framesFor(index) {
    const slide = this.#slideAt(index);
    const clones = this.#clones;
    const { slides } = this;

    if (!slide) return [];
    if (!clones || !slides?.length) return [slide];
    if (slide === slides[0]) return [slide, clones.tail];
    if (slide === slides[slides.length - 1]) return [slide, clones.lead];

    return [slide];
  }

  /**
   * Starts fetching a slide's images rather than leaving them to the browser's lazy loading,
   * which for a slide outside the frame only begins as the slideshow arrives at it. At autoplay
   * speed on a slow connection that is too late — the fade lands on an image that hasn't turned
   * up yet — so the slide that is next in line is warmed as soon as it becomes next.
   * @param {number} index - Slide index; wraps.
   */
  #warmSlide(index) {
    for (const frame of this.#framesFor(index)) {
      for (const image of frame.querySelectorAll('img[loading="lazy"]')) {
        image.loading = 'eager';
      }
    }
  }

  /**
   * Whether a slide's images have all arrived. Images that failed to load count as arrived:
   * waiting on one that is never coming would stop autoplay for good.
   * @param {number} index - Slide index; wraps.
   */
  #slideMediaReady(index) {
    return this.#framesFor(index).every((frame) =>
      [...frame.querySelectorAll('img')].every((image) => image.complete)
    );
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The timer for the next automatic advance, if one is pending.
   * @type {ReturnType<typeof setTimeout>|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * The two copies parked either side of the real slides: `lead` is a copy of the last
   * slide sitting before the first, `tail` a copy of the first sitting after the last.
   * They are what gives a scroller with a fixed start and end somewhere to go when a
   * gesture carries on past either edge.
   * @type {{lead: HTMLElement, tail: HTMLElement} | null}
   */
  #clones = null;

  /**
   * Whether this slideshow loops by scrolling into a boundary clone rather than by jumping
   * back across all its slides. Only worth doing where exactly one slide fills the frame,
   * so it is opted into by the sections that lay out that way.
   */
  get seamless() {
    return this.hasAttribute('seamless-loop') && this.infinite;
  }

  /**
   * Whether the slides are stacked and fade between one another instead of scrolling. The
   * scroller never moves in this mode, so the slideshow tracks the current slide itself and
   * handles swipes itself.
   */
  get crossfade() {
    return this.hasAttribute('crossfade');
  }

  /**
   * Puts a copy of the last slide before the first and a copy of the first after the last,
   * so the scroller has a slide's worth of room past each end. A swipe, a trackpad or a
   * drag can then carry on straight through the boundary — there is real content under the
   * finger the whole way — and the slideshow lands back on the matching real slide
   * afterwards without anything having moved on screen.
   */
  #buildLoopClones() {
    const slides = this.refs.slides;

    if (this.#clones || !this.seamless || !slides || slides.length < 2) return;

    const { scroller } = this.refs;
    const first = slides[0];
    const last = slides[slides.length - 1];

    if (!first || !last) return;

    const lead = this.#cloneSlide(last, CLONE_TIMELINES.lead);
    const tail = this.#cloneSlide(first, CLONE_TIMELINES.tail);

    scroller.prepend(lead);
    scroller.append(tail);

    // Each clone crossfades on a view timeline of its own, and a timeline is only visible
    // to the elements animating on it if the component has brought its name into scope.
    const scope = this.style.getPropertyValue('--slideshow-timeline');

    if (!scope.includes(CLONE_TIMELINES.lead)) {
      this.style.setProperty('--slideshow-timeline', `${scope}, ${CLONE_TIMELINES.lead}, ${CLONE_TIMELINES.tail}`);
    }

    // The first slide no longer sits at the start of the scroller, so resting at zero would
    // leave the page opening on the lead clone. Nothing is listening for scrolls yet, and
    // the scroller's own smooth behaviour has to be overridden or this would animate.
    scroller.scrollTo({ left: first.offsetLeft, top: first.offsetTop, behavior: 'instant' });

    this.#clones = { lead, tail };
  }

  /**
   * Copies a slide into scenery: something that paints and scrolls exactly like the slide it
   * came from, but that nothing else in the page can reach — not the ref system, the
   * accessibility tree, the tab order, the theme editor, or a click.
   * @param {HTMLElement} slide - The slide to copy.
   * @param {string} timeline - The view timeline name the copy animates on.
   * @returns {HTMLElement}
   */
  #cloneSlide(slide, timeline) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));

    clone.setAttribute(CLONE_ATTRIBUTE, '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('on:click');
    clone.removeAttribute('data-shopify-editor-block');

    for (const element of clone.querySelectorAll('[ref], [id], [on\\:click], [data-shopify-editor-block]')) {
      element.removeAttribute('ref');
      element.removeAttribute('id');
      element.removeAttribute('on:click');
      element.removeAttribute('data-shopify-editor-block');
    }

    for (const element of clone.querySelectorAll('a, button, input, textarea, select, summary, [tabindex]')) {
      element.setAttribute('tabindex', '-1');
    }

    // A copy of the first slide would otherwise inherit its eagerness and compete with the
    // real one for the opening of the page, where it is the furthest thing from the frame.
    // It is warmed like any other slide once the slideshow is a step away from needing it.
    for (const image of clone.querySelectorAll('img')) {
      image.loading = 'lazy';
      image.fetchPriority = 'low';
    }

    clone.style.setProperty('--slideshow-timeline', timeline);

    return clone;
  }

  /**
   * The places the scroller comes to rest — one per slide, plus the two boundary clones —
   * as offsets along its axis, each paired with the slide it leaves the slideshow on.
   * @returns {{offset: number, index: number, element: HTMLElement, clone: boolean}[]}
   */
  #restingPositions() {
    const { slides } = this;

    if (!slides?.length || !this.#scroll) return [];

    const edge = this.#scroll.axis === 'x' ? 'offsetLeft' : 'offsetTop';
    const positions = slides.map((element, index) => ({ offset: element[edge], index, element, clone: false }));
    const clones = this.#clones;

    if (clones) {
      positions.unshift({ offset: clones.lead[edge], index: slides.length - 1, element: clones.lead, clone: true });
      positions.push({ offset: clones.tail[edge], index: 0, element: clones.tail, clone: true });
    }

    return positions;
  }

  /**
   * Which of those resting positions the scroller is nearest to right now.
   * @returns {{at: number, positions: {offset: number, index: number, element: HTMLElement, clone: boolean}[]} | null}
   */
  #nearestRestingPosition() {
    const positions = this.#restingPositions();
    const { scroller } = this.refs;

    if (!positions.length || !this.#scroll || !scroller) return null;

    const offset = this.#scroll.axis === 'x' ? scroller.scrollLeft : scroller.scrollTop;

    let at = 0;

    for (const [index, position] of positions.entries()) {
      const nearest = positions[at];
      if (nearest && Math.abs(position.offset - offset) < Math.abs(nearest.offset - offset)) at = index;
    }

    return { at, positions };
  }

  /**
   * Moves the scroller off a boundary clone and onto the real slide it copies, once it has
   * come to rest there — however it got there: a swipe, a trackpad, a drag, an arrow. The
   * two are at the same point in their own view timelines while either is centred, so this
   * is invisible; it is only what makes the next scroll in either direction have room.
   */
  #settleOnClone() {
    // Never while a finger or a pointer is still on it. Touch scrolling holds still often
    // enough — mid-swipe, between two flicks — to look finished to a scroll-end timer, and
    // moving the scroller then would take the slideshow out from under the gesture.
    if (!this.#clones || this.#dragging || this.#touching || !this.#scroll) return;

    const nearest = this.#nearestRestingPosition();
    const rest = nearest?.positions[nearest.at];

    if (!rest?.clone) return;

    const slide = this.slides?.[rest.index];

    if (!slide) return;

    const previousIndex = this.current;

    this.#scroll.to(slide, { instant: true });

    // The intersection observer only watches the real slides, so while the frame was on a
    // clone it had nothing to report and the scroll that just ended will have been told
    // that no slide is showing. Say which one is, rather than leaving the slide the frame
    // is now on marked hidden — and unclickable with it — until the observer catches up.
    this.#visibleSlides = [slide];
    slide.setAttribute('aria-hidden', 'false');
    this.current = rest.index;

    if (previousIndex === rest.index) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: rest.index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Puts the scroller back exactly on the slide the slideshow believes it is on, without
   * animating. A no-op unless the two have drifted apart, which only the boundary clones
   * make possible — they move where every slide sits in the scroller.
   */
  #anchor() {
    if (!this.#clones || this.#dragging || this.#touching || this.#disabled) return;

    const slide = this.slides?.[this.current];

    if (slide) this.#scroll?.to(slide, { instant: true });
  }

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Before anything measures the scroller: the boundary clones change where every slide
    // sits in it, the first one included.
    this.#buildLoopClones();

    // Setup IntersectionObserver first for efficient visibility tracking. Stacked slides
    // are all in view at once, so a crossfade tracks the one showing itself instead.
    if (this.crossfade) {
      const initial = this.slides?.[this.initialSlideIndex] ?? this.slides?.[0];
      this.#visibleSlides = initial ? [initial] : [];
    } else {
      this.#setupIntersectionObserver();
    }

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    if (this.crossfade) {
      scroller.addEventListener('pointerdown', this.#handleCrossfadePointerDown);
      scroller.addEventListener('dragstart', preventDefault);
    } else {
      // Ahead of the mousedown listener below in every case, which is what lets that one tell
      // a real mouse from a touchscreen's compatibility events.
      scroller.addEventListener('pointerdown', this.#handleScrollerPointerDown, { passive: true });
      scroller.addEventListener('mousedown', this.#handleMouseDown);

      // Touch scrolling is the browser's, so looping past the last slide is the one part of
      // it the component has to spot for itself. The move listener only measures, so it stays
      // passive and out of the way of the scrolling it is watching.
      scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      scroller.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
      scroller.addEventListener('touchend', this.#handleTouchEnd);
      scroller.addEventListener('touchcancel', this.#handleTouchEnd);
    }

    this.addEventListener('mouseenter', this.#handlePointerRest);
    this.addEventListener('mouseleave', this.#handlePointerLeave);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          // With a clone parked before it, the first slide is no longer where the scroller
          // rests by default. Anchoring is normally select()'s job, but it declines to move
          // to a slide the slideshow already believes it is on.
          this.#anchor();

          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        // Every resting place moves when the frame is resized, and the clones make being a
        // frame out mean showing the wrong slide rather than merely sitting off-centre.
        this.#anchor();

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    // select() moves `current` before it scrolls, so a scroll that changes the slide is the
    // visitor's own — a swipe or a trackpad. The slide they chose gets a full interval.
    this.resume();

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    // Before the visibility sweep, so that a scroll ending on a boundary clone is already
    // back on the real slide by the time the sweep works out what is on screen.
    this.#settleOnClone();
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /** Whether a finger is currently down on the scroller. */
  #touching = false;

  /**
   * What kind of pointer last went down on the scroller. A tap on a touchscreen is followed
   * by a compatibility `mousedown`, and that arrives after the gesture's `pointerup` has
   * already been and gone — so a drag started from it would never be told to stop.
   * @type {string}
   */
  #pointerType = 'mouse';

  /**
   * The live state of a touch gesture: where it was last seen, where the scroller stood when
   * it began, how far it has travelled along each axis, which way it is currently going, and
   * how much of that travel the scroller could not act on because it had run out of room.
   * @type {{x: number, y: number, scroll: number, travelled: number, drifted: number, overSwipe: number, forward: boolean} | null}
   */
  #touchGesture = null;

  /**
   * Whether the scroller runs right to left, which reverses both what a forward swipe looks
   * like and which way `scrollLeft` counts. Read once per gesture: resolving it is a style
   * lookup, and touchmove fires far too often to pay for one each time.
   */
  #inlineReversed = false;

  /**
   * How far the scroller is scrolled and how far it can go, counted from the start in the
   * scroller's own direction.
   * @type {{position: number, max: number} | null}
   */
  get #scrollProgress() {
    const { scroller } = this.refs;

    if (!scroller || !this.#scroll) return null;

    const horizontal = this.#scroll.axis === 'x';
    const offset = horizontal ? scroller.scrollLeft : scroller.scrollTop;

    // `scrollLeft` counts down from zero in a right-to-left document, so flip it there. The
    // sign is kept rather than taken as a magnitude because elastic overscroll pushes the
    // offset past both ends, and a finger stretching past the start has to read as before
    // the start rather than as beyond the end.
    const position = horizontal && this.#inlineReversed ? -offset : offset;
    const max = horizontal
      ? scroller.scrollWidth - scroller.clientWidth
      : scroller.scrollHeight - scroller.clientHeight;

    return { position, max };
  }

  /** Whether the scroller has nowhere left to go in the forward direction. */
  get #atScrollEnd() {
    const progress = this.#scrollProgress;

    return progress ? progress.position >= progress.max - SCROLL_BOUNDARY_TOLERANCE : false;
  }

  /** Whether the scroller has nowhere left to go in the backward direction. */
  get #atScrollStart() {
    const progress = this.#scrollProgress;

    return progress ? progress.position <= SCROLL_BOUNDARY_TOLERANCE : false;
  }

  /**
   * Reads which way the scroller runs, once at the start of a gesture.
   */
  #readInlineDirection() {
    const { scroller } = this.refs;

    this.#inlineReversed =
      this.#scroll?.axis === 'x' && scroller ? getComputedStyle(scroller).direction === 'rtl' : false;
  }

  /**
   * Starts tracking a touch gesture.
   * @param {TouchEvent} event - The touchstart event.
   */
  #handleTouchStart = (event) => {
    this.#touchGesture = null;
    this.#touching = true;

    // Hold autoplay while a finger is on the slideshow; lifting it starts a fresh interval.
    this.suspend();

    // Touch events keep the target they started on for the whole gesture, so a swipe that
    // belongs to a nested slideshow bubbles up to this one. It stays the nested
    // slideshow's to answer.
    if (event.target instanceof Element && event.target.closest('slideshow-component') !== this) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    this.#readInlineDirection();

    this.#touchGesture = {
      x: touch.clientX,
      y: touch.clientY,
      scroll: this.#scrollProgress?.position ?? 0,
      travelled: 0,
      drifted: 0,
      overSwipe: 0,
      forward: true,
    };
  };

  /**
   * Measures how far the finger keeps going after the scroller has run out of room. That
   * part of the swipe is the only part the browser can't act on, so it's what tells us the
   * gesture was asking to go past the slide at the end.
   * @param {TouchEvent} event - The touchmove event.
   */
  #handleTouchMove = (event) => {
    const gesture = this.#touchGesture;

    if (!gesture || !this.#scroll) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const horizontal = this.#scroll.axis === 'x';
    const step = horizontal ? gesture.x - touch.clientX : gesture.y - touch.clientY;
    const drift = horizontal ? gesture.y - touch.clientY : gesture.x - touch.clientX;

    gesture.x = touch.clientX;
    gesture.y = touch.clientY;
    gesture.travelled += step;
    gesture.drifted += drift;

    if (!step) return;

    const forward = this.#inlineReversed ? step < 0 : step > 0;
    const pinned = forward ? this.#atScrollEnd : this.#atScrollStart;

    // Any movement the scroller could act on is ordinary scrolling, and so is a change of
    // direction: either way the count starts again.
    if (!pinned) {
      gesture.overSwipe = 0;
      return;
    }

    if (forward !== gesture.forward) gesture.overSwipe = 0;

    gesture.forward = forward;
    gesture.overSwipe += Math.abs(step);
  };

  /**
   * Loops an infinite slideshow around when a swipe carried on past the slide at either
   * end. The scroller is as far as it goes there, so the browser has nothing left to
   * scroll and the gesture would otherwise die against the edge.
   * @param {TouchEvent} event - The touchend or touchcancel event.
   */
  #handleTouchEnd = (event) => {
    const gesture = this.#touchGesture;
    this.#touchGesture = null;
    this.#touching = false;

    this.resume();

    // A seamless slideshow has real content past both ends, so a swipe that carries on
    // never runs out of room and there is nothing here to rescue: the browser scrolls it
    // and snaps it like any other. All that is left is to see where it came to rest, and
    // that has to wait out whatever momentum the lift set going.
    if (this.seamless && this.#clones) {
      this.#scroll?.finished.then(() => this.#settleOnClone());
      return;
    }

    if (!gesture || !this.infinite || this.disabled || this.#dragging || !this.#scroll) return;
    if (!this.slides || this.slides.length <= 1) return;

    const touch = event.changedTouches[0];
    const horizontal = this.#scroll.axis === 'x';

    // Where the finger ended up, and where the scroller ended up. Both are read now that the
    // gesture is over, which is the one moment they are dependably in step with each other.
    const travelled = touch
      ? (horizontal ? gesture.x - touch.clientX : gesture.y - touch.clientY) + gesture.travelled
      : gesture.travelled;
    const drifted = touch
      ? (horizontal ? gesture.y - touch.clientY : gesture.x - touch.clientX) + gesture.drifted
      : gesture.drifted;
    const scrolled = Math.abs((this.#scrollProgress?.position ?? 0) - gesture.scroll);

    const forward = this.#inlineReversed ? travelled < 0 : travelled > 0;

    // Enough of the swipe has to be travel the scroller couldn't act on, which is what asking
    // to go past the slide at the end looks like. A swipe that merely arrived at the last
    // slide is an ordinary scroll, and the browser has already carried it out.
    //
    // Two ways of measuring the same thing, because browsers differ on how much of a gesture
    // they report while they are busy scrolling through it: what the moves added up to, and
    // what the whole gesture asked for beyond what the scroller delivered. Either will do,
    // as long as it was pushing the way the gesture as a whole ended up pushing.
    const measured = gesture.forward === forward ? gesture.overSwipe : 0;
    const unused = Math.abs(travelled) - scrolled;

    if (Math.max(measured, unused) < OVER_SWIPE_THRESHOLD) return;

    // And it has to have been a swipe along the slideshow's own axis, rather than a scroll of
    // the page that drifted sideways on the way.
    if (Math.abs(travelled) <= Math.abs(drifted)) return;

    // Releasing away from that edge means the browser found room after all.
    if (forward ? !this.#atScrollEnd : !this.#atScrollStart) return;

    this.select(forward ? this.nextIndex : this.previousIndex, event);
  };

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Touch scrolls the slideshow by itself, and the only `mousedown` a touchscreen ever
    // sends is the compatibility one after a tap — which lands after that gesture's
    // `pointerup`, so the drag it would start here has no event left that would end it.
    // It would sit there with snapping switched off, and the next swipe would run free.
    if (this.#pointerType === 'touch') return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    // How much of the drag the scroller couldn't act on because it had already run out of
    // room, and which way that part was pushing.
    let overSwipe = 0;
    let overSwipeForward = true;

    this.#dragging = true;
    this.#readInlineDirection();

    // Where the drag set off from, among the places the scroller rests. A drag that ends
    // somewhere else has already chosen its slide; one that ends where it began is a flick,
    // and needs the nudge onwards below.
    const startRest = this.#nearestRestingPosition()?.at ?? -1;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        // Hold autoplay for the length of the drag — not `pause()`, which is the visitor
        // pressing the pause button and stays pressed, so `resume()` below would decline.
        this.suspend();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });

      // Whatever the scroller couldn't move through is the pointer asking to go past the
      // slide at the end. Anything it could move through is ordinary scrolling, and so is a
      // change of direction: either way the count starts again.
      if (delta) {
        const forward = this.#inlineReversed ? delta < 0 : delta > 0;
        const pinned = forward ? this.#atScrollEnd : this.#atScrollStart;

        if (!pinned) {
          overSwipe = 0;
        } else {
          if (forward !== overSwipeForward) overSwipe = 0;
          overSwipeForward = forward;
          overSwipe += Math.abs(delta);
        }
      }
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const flicked = Math.abs(velocity) >= 10 && distanceTravelled >= 10;

      // A seamless slideshow settles by scroll position rather than by slide index, because
      // the drag may well have ended on one of the boundary clones — which is no index at
      // all, and is exactly what carrying on past the last slide is meant to feel like.
      // Snapping to the nearest resting place lands it there; #settleOnClone then moves it
      // onto the real slide that clone copies, unseen.
      if (this.seamless && this.#clones) {
        const nearest = this.#nearestRestingPosition();
        const positions = nearest?.positions ?? [];
        const landed = nearest && nearest.at === startRest && flicked ? nearest.at + direction : nearest?.at;
        const rest = positions[clamp(landed ?? 0, 0, positions.length - 1)];

        this.removeAttribute('dragging');
        if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

        if (rest) {
          const previousIndex = this.current;

          this.#scroll.to(rest.element);
          this.#centerSelectedThumbnail(rest.index);
          this.current = rest.index;

          if (previousIndex !== rest.index) {
            const slide = slides[rest.index];

            this.dispatchEvent(
              new SlideshowSelectEvent({
                index: rest.index,
                previousIndex,
                userInitiated: true,
                trigger: 'drag',
                slide,
                id: slide?.getAttribute('slide-id'),
              })
            );
          }
        }

        await this.#scroll.finished;

        if (this.#dragging) return;

        // A drag that ended already sitting on a clone moves the scroller nowhere, so there
        // is no scroll for the usual scroll-end handling to hang off. Settle it here, while
        // snapping is still off and cannot argue with the move.
        this.#settleOnClone();

        this.#scroll.snap = true;
        this.resume();

        return;
      }

      const next = this.#sync();

      const modifier = current !== next || !flicked ? 0 : direction;
      const target = next + modifier;

      // A drag that carries on past the slide at either end loops around, the way the arrows
      // and autoplay already do on an infinite slideshow. Either the flick resolved past the
      // end, or the pointer kept pushing at an edge the scroller had no room left to move
      // through — a slow drag never builds the velocity the flick test looks for, since that
      // velocity is measured across the whole drag.
      let loopTarget = null;

      if (this.infinite) {
        if (target < 0 || target > slides.length - 1) {
          loopTarget = target;
        } else if (
          overSwipe >= OVER_SWIPE_THRESHOLD &&
          (overSwipeForward ? this.#atScrollEnd : this.#atScrollStart)
        ) {
          loopTarget = overSwipeForward ? this.nextIndex : this.previousIndex;
        }
      }

      this.removeAttribute('dragging');
      // Only what was actually captured. Releasing a pointer that is already gone throws,
      // and everything below — restoring scroll snapping most of all — would be skipped.
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

      if (loopTarget !== null) {
        // select() handles the jump: it reorders the slides so the loop stays a short
        // scroll in the direction of the drag instead of racing back across all of them.
        await this.select(loopTarget, event);
      } else {
        const newIndex = clamp(target, 0, slides.length - 1);
        const newSlide = slides[newIndex];
        const currentIndex = this.current;

        if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

        this.#scroll.to(newSlide);

        this.#centerSelectedThumbnail(newIndex);

        this.dispatchEvent(
          new SlideshowSelectEvent({
            index: newIndex,
            previousIndex: currentIndex,
            userInitiated: true,
            trigger: 'drag',
            slide: newSlide,
            id: newSlide.getAttribute('slide-id'),
          })
        );

        this.current = newIndex;
      }

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  /**
   * A swipe across a crossfading slideshow. There is no scroller to drag, so instead the
   * slide the swipe is heading for fades in over the current one as the finger travels —
   * the gesture visibly does something the whole way — and on release the fade either
   * finishes from wherever it got to or falls back. A mostly vertical gesture is left to
   * the page; with `touch-action: pan-y` the browser cancels the pointer as soon as it
   * takes one over for scrolling.
   * @param {PointerEvent} event
   */
  #handleCrossfadePointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0) return;
    if (this.disabled || (this.slides?.length ?? 0) <= 1) return;
    if (event.target instanceof Element && event.target.closest('slideshow-component') !== this) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const width = this.refs.scroller.clientWidth || 1;
    const reversed = getComputedStyle(this.refs.scroller).direction === 'rtl';
    const controller = new AbortController();
    const { signal } = controller;

    /** Whether the gesture has shown itself to be a horizontal swipe. */
    let swiping = false;

    /** @type {HTMLElement | null} */
    let preview = null;

    this.suspend();

    /** @param {PointerEvent} event */
    const isForward = (event) => (reversed ? event.clientX > startX : event.clientX < startX);

    /** @param {HTMLElement} slide */
    const clearPreview = (slide) => {
      slide.style.removeProperty('opacity');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('z-index');
      slide.style.removeProperty('transition');
    };

    /** @param {PointerEvent} event */
    const onMove = (event) => {
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!swiping) {
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
        swiping = true;
      }

      const target = this.#slideAt(isForward(event) ? this.current + 1 : this.current - 1);

      if (!target) return;

      // Changing direction mid-swipe swaps which neighbour is coming in.
      if (preview && preview !== target) clearPreview(preview);

      preview = target;
      preview.style.transition = 'none';
      preview.style.visibility = 'visible';
      preview.style.zIndex = '2';
      preview.style.opacity = `${Math.min(Math.abs(dx) / (width * 0.6), 1)}`;
    };

    /** @param {PointerEvent} event */
    const onEnd = (event) => {
      controller.abort();

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const swiped =
        event.type === 'pointerup' && Math.abs(dx) >= CROSSFADE_SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy);

      if (!swiped) {
        // Fall back out from wherever the preview had got to, then hand the slide back to
        // the stylesheet.
        const slide = preview;

        if (slide) {
          slide.style.transition = 'opacity 0.3s ease, visibility 0s linear 0.3s';
          slide.style.opacity = '0';
          slide.style.visibility = 'hidden';
          setTimeout(() => clearPreview(slide), 350);
        }

        return this.resume();
      }

      // A swipe isn't a click on whatever it started over — a link, or the slide itself,
      // whose own click handler would select it straight back. The click, if one comes, is
      // dispatched straight after this pointerup, so the guard only has to outlast that.
      /** @param {Event} event */
      const swallowClick = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      // On the window, because the theme's `on:click` delegation already sits on the
      // document's capture phase and would run first.
      window.addEventListener('click', swallowClick, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', swallowClick, { capture: true }));

      // Dropping the inline opacity in the same frame as the slide becomes current lets the
      // stylesheet's fade carry on from the opacity the finger left it at, not from zero.
      if (preview) clearPreview(preview);

      if (isForward(event)) this.next(event);
      else this.previous(event);
    };

    document.addEventListener('pointermove', onMove, { signal });
    document.addEventListener('pointerup', onEnd, { signal });
    document.addEventListener('pointercancel', onEnd, { signal });
  };

  /**
   * @param {PointerEvent} event
   */
  #handleScrollerPointerDown = (event) => {
    this.#pointerType = event.pointerType;
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');

    // Someone is about to move through the slides, and either direction is now one gesture
    // away. `current` only warms its neighbours when it changes, and the slideshow opens on
    // the slide it is already set to, so until now nothing has asked for the slide behind
    // the first one — or for the boundary clone that stands in for it.
    this.#warmSlide(this.current + 1);
    this.#warmSlide(this.current - 1);
  };

  /**
   * Holds autoplay while the pointer is on the slideshow — but only on the devices that
   * really have one. A tap on a touchscreen raises the same events and can leave them
   * unmatched, which would stop the slideshow for the rest of the visit.
   */
  #handlePointerRest = () => {
    if (this.#hoverCapable) this.suspend();
  };

  #handlePointerLeave = () => {
    if (this.#hoverCapable) this.resume();
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
